import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpacerService, UpdateSpacerRedesignReasonRequestDto, TestTypes } from '../../../services/spacer.service';

// Enum mapping to match backend SpacerRedesignReasonTypes
enum SpacerRedesignReasonTypes {
  Compliance = 1,
  Logistics = 2,
  Cost = 3
}

interface TestCriteria {
  spacerCompatibility: boolean;
  spacerSSST: boolean;
  spacerQAQC: boolean;
  spacerHPHTRheology: boolean;
  mixability: boolean;
}

interface RedesignData {
  isRedesign: boolean;
  reason: string;
  testCriteria: TestCriteria;
  comments: string;
}

@Component({
  selector: 'app-redesign',
  imports: [CommonModule, FormsModule],
  templateUrl: './redesign.component.html',
  styleUrls: ['./redesign.component.css']
})
export class RedesignComponent implements OnInit {
  @Input() spacerId: number | null = null;

  redesignData: RedesignData = {
    isRedesign: false,
    reason: '',
    testCriteria: {
      spacerCompatibility: false,
      spacerSSST: false,
      spacerQAQC: false,
      spacerHPHTRheology: false,
      mixability: false
    },
    comments: ''
  };

  isLoading = false;
  errorMessage = '';
  successMessage = '';
  
  // Track the initial state of isRedesign when component loads
  private initialIsRedesignState = false;

  constructor(private spacerService: SpacerService) {}

  ngOnInit(): void {
    if (this.spacerId) {
      this.loadRedesign();
    }
  }

  loadRedesign(): void {
    if (!this.spacerId) {
      return;
    }

    // Load spacer with tests and redesign data
    this.spacerService.getByIdWithTestsLoaded(this.spacerId).subscribe({
      next: (spacer) => {
        // Check if there's existing redesign data
        if (spacer.redesignReason && spacer.redesignReason.reasonTypeId) {
          // Existing redesign data found
          this.initialIsRedesignState = true;
          
          // Populate the form with existing data
          this.redesignData.isRedesign = true;
          this.redesignData.reason = this.getReasonName(spacer.redesignReason.reasonTypeId);
          this.redesignData.comments = spacer.redesignReason.comments || '';
          
          // Populate compliance tests if they exist
          if (spacer.redesignReason.complianceTestIds && spacer.redesignReason.complianceTestIds.length > 0) {
            spacer.redesignReason.complianceTestIds.forEach(testId => {
              switch (testId) {
                case TestTypes.SpacerCompatibility:
                  this.redesignData.testCriteria.spacerCompatibility = true;
                  break;
                case TestTypes.SpacerSSST:
                  this.redesignData.testCriteria.spacerSSST = true;
                  break;
                case TestTypes.SpacerQAQC:
                  this.redesignData.testCriteria.spacerQAQC = true;
                  break;
                case TestTypes.SpacerHpHtRheology:
                  this.redesignData.testCriteria.spacerHPHTRheology = true;
                  break;
                case TestTypes.Mixability:
                  this.redesignData.testCriteria.mixability = true;
                  break;
              }
            });
          }
        } else {
          // No existing redesign data - this will be first time if they enable it
          this.initialIsRedesignState = false;
          
          // Initialize with default values
          this.redesignData = {
            isRedesign: false,
            reason: '',
            testCriteria: {
              spacerCompatibility: false,
              spacerSSST: false,
              spacerQAQC: false,
              spacerHPHTRheology: false,
              mixability: false
            },
            comments: ''
          };
        }
      },
      error: (error) => {
        console.error('Error loading spacer details:', error);
        this.initialIsRedesignState = false;
      }
    });
  }

  private getReasonName(reasonTypeId: number): string {
    switch (reasonTypeId) {
      case SpacerRedesignReasonTypes.Compliance:
        return 'Compliance';
      case SpacerRedesignReasonTypes.Logistics:
        return 'Logistics';
      case SpacerRedesignReasonTypes.Cost:
        return 'Cost';
      default:
        return '';
    }
  }

  updateRedesign(): void {
    if (!this.spacerId) {
      this.errorMessage = 'Spacer ID is required';
      return;
    }

    // Validate redesign data
    if (this.redesignData.isRedesign) {
      if (!this.redesignData.reason) {
        this.errorMessage = 'Please select a reason for the redesign';
        return;
      }

      if (this.redesignData.reason === 'Compliance') {
        const hasSelectedTests = Object.values(this.redesignData.testCriteria).some(val => val === true);
        if (!hasSelectedTests) {
          this.errorMessage = 'Please select at least one compliance test';
          return;
        }
      }
    }

    // Determine parentSlurryId:
    // - If this is the first time enabling redesign (was false, now true), set parentSlurryId = slurryId
    // - Otherwise, set to null (backend will preserve existing value or handle appropriately)
    const isFirstTimeEnabling = !this.initialIsRedesignState && this.redesignData.isRedesign;
    const parentSlurryId = isFirstTimeEnabling ? this.spacerId : null;

    // Build the request DTO
    const request: UpdateSpacerRedesignReasonRequestDto = {
      slurryId: this.spacerId,
      isRedesign: this.redesignData.isRedesign,
      reasonType: this.getReasonTypeId(),
      comments: this.redesignData.comments || null,
      parentSlurryId: this.spacerId,
      complianceTests: this.getComplianceTestIds()
    };

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.spacerService.updateRedesignReason(this.spacerId, request).subscribe({
      next: () => {
        this.isLoading = false;
        this.successMessage = 'Redesign reason updated successfully';
        console.log('Redesign reason updated successfully');
        
        // Update the initial state to reflect current state
        this.initialIsRedesignState = this.redesignData.isRedesign;
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error.error?.detail || error.message || 'Failed to update redesign reason';
        console.error('Failed to update redesign reason:', error);
      }
    });
  }

  private getReasonTypeId(): number | null {
    if (!this.redesignData.isRedesign || !this.redesignData.reason) {
      return null;
    }

    switch (this.redesignData.reason) {
      case 'Compliance':
        return SpacerRedesignReasonTypes.Compliance;
      case 'Logistics':
        return SpacerRedesignReasonTypes.Logistics;
      case 'Cost':
        return SpacerRedesignReasonTypes.Cost;
      default:
        return null;
    }
  }

  private getComplianceTestIds(): number[] {
    if (!this.redesignData.isRedesign || this.redesignData.reason !== 'Compliance') {
      return [];
    }

    const testIds: number[] = [];
    
    if (this.redesignData.testCriteria.spacerCompatibility) {
      testIds.push(TestTypes.SpacerCompatibility);
    }
    if (this.redesignData.testCriteria.spacerSSST) {
      testIds.push(TestTypes.SpacerSSST);
    }
    if (this.redesignData.testCriteria.spacerQAQC) {
      testIds.push(TestTypes.SpacerQAQC);
    }
    if (this.redesignData.testCriteria.spacerHPHTRheology) {
      testIds.push(TestTypes.SpacerHpHtRheology);
    }
    if (this.redesignData.testCriteria.mixability) {
      testIds.push(TestTypes.Mixability);
    }

    return testIds;
  }
}
